/**
 * Capture de `beforeinstallprompt` à la portée du module — pas dans un
 * composant : le navigateur peut déclencher l'événement avant même que le
 * premier composant intéressé soit monté, et le rater ici retomberait sur
 * des instructions manuelles alors qu'un flux natif en un clic était
 * disponible.
 *
 * memoneo fait la même capture directement dans son composant
 * InstallAppNudge.tsx, qui est l'unique consommateur là-bas. Ici il y en a
 * deux — la carte permanente des réglages (InstallAppCard) et le bandeau
 * discret (InstallAppNudge) — d'où un module dédié plutôt qu'une
 * duplication de l'écouteur et de l'état module-level dans les deux.
 */
import { useSyncExternalStore } from "react";

/** Le DOM lib standard ne type pas cet événement — Chrome/Edge seulement. */
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
  prompt(): Promise<void>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const subscribers = new Set<() => void>();

function notifySubscribers(): void {
  subscribers.forEach((cb) => cb());
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    // Empêche le mini-infobar natif de Chrome : ce sont nos propres surfaces
    // (carte + bandeau) qui pilotent l'invite, sur leur propre timing.
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    notifySubscribers();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    notifySubscribers();
  });
}

/** Lecture réactive de l'événement capté au niveau module — re-render dès
 *  qu'il arrive, même s'il arrive après le montage du composant appelant. */
export function useDeferredInstallPrompt(): BeforeInstallPromptEvent | null {
  return useSyncExternalStore(
    (cb) => {
      subscribers.add(cb);
      return () => subscribers.delete(cb);
    },
    () => deferredPrompt,
    () => null,
  );
}

/** Déclenche l'invite native retenue et attend le choix de l'utilisateur.
 *  `"unavailable"` si aucun événement n'a été capté (appelant négligent, ou
 *  course avec `appinstalled`) — les appelants gardent de toute façon leur
 *  bouton caché tant que `useDeferredInstallPrompt()` renvoie `null`. */
export async function triggerInstallPrompt(): Promise<"accepted" | "dismissed" | "unavailable"> {
  const event = deferredPrompt;
  if (!event) return "unavailable";
  await event.prompt();
  const choice = await event.userChoice;
  if (choice.outcome === "accepted") {
    deferredPrompt = null;
    notifySubscribers();
  }
  return choice.outcome;
}
